/**
 * Frontend API client.
 *
 * Thin wrapper around `fetch` that:
 *  - Prepends the build-time `VITE_API_BASE_URL` to every request path.
 *  - Reads/writes the JWT in `localStorage` under the key `'token'`.
 *  - Automatically attaches `Authorization: Bearer ${token}` to protected calls.
 *  - On any 401 response: clears the stored token and schedules a redirect to
 *    `/admin/login` within 1s (guarded for non-browser test environments).
 *
 * Validates: Requirements 8.7, 8.8 (and the surface of Properties 19, 20).
 */

import { API_BASE_URL } from '../config/env';
import type {
  ApiErrorPayload,
  Captain,
  DeleteResponse,
  LoginResponse,
  Paginated,
  PublicCaptain,
} from './types';

const TOKEN_STORAGE_KEY = 'token';
const REDIRECT_PATH = '/admin/login';
const REDIRECT_DELAY_MS = 1;

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * Generic API error thrown for any non-2xx response (other than 401, which
 * surfaces as `UnauthorizedError`). Carries the HTTP status, an optional
 * offending field name, and an optional backend error code.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly field?: string;
  readonly code?: string;

  constructor(message: string, status: number, field?: string, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    if (field !== undefined) this.field = field;
    if (code !== undefined) this.code = code;
  }
}

/**
 * Specialized error for 401 responses. Callers can branch on `instanceof
 * UnauthorizedError` to surface session-expired UI without inspecting status
 * codes directly.
 */
export class UnauthorizedError extends ApiError {
  constructor(message = 'الجلسة غير صالحة') {
    super(message, 401);
    this.name = 'UnauthorizedError';
  }
}

// ---------------------------------------------------------------------------
// Token storage helpers
// ---------------------------------------------------------------------------

/**
 * Returns the persisted JWT, or `null` if none is stored / `localStorage` is
 * unavailable (SSR/tests).
 */
export function getToken(): string | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Persists the JWT in `localStorage` under the canonical key.
 */
export function setToken(token: string): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch {
    /* ignore quota / disabled-storage errors */
  }
}

/**
 * Removes the persisted JWT.
 */
export function clearToken(): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Internal request plumbing
// ---------------------------------------------------------------------------

interface RequestOptions {
  /** Whether to attach the `Authorization` header from `localStorage`. */
  requireAuth?: boolean;
  /** JSON-serialisable body. Mutually exclusive with `formData`. */
  json?: unknown;
  /** Multipart body. Mutually exclusive with `json`. */
  formData?: FormData;
  /** Expected response body shape; defaults to JSON. */
  responseType?: 'json' | 'blob';
}

function joinUrl(base: string, path: string): string {
  const trimmedBase = base.replace(/\/+$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${trimmedBase}${normalizedPath}`;
}

/**
 * Handles a 401 response: drop the stored token and schedule a redirect to
 * the login page. The redirect is wrapped in `setTimeout` (1ms) so that the
 * caller's promise can reject first, and is guarded for environments without
 * a `window` (e.g. Node test runners without jsdom).
 */
function handleUnauthorized(): void {
  clearToken();
  if (typeof window !== 'undefined' && window.location) {
    setTimeout(() => {
      try {
        window.location.href = REDIRECT_PATH;
      } catch {
        /* ignore navigation errors in test environments */
      }
    }, REDIRECT_DELAY_MS);
  }
}

async function parseErrorBody(response: Response): Promise<ApiErrorPayload> {
  try {
    const data = (await response.json()) as ApiErrorPayload;
    if (data && typeof data === 'object' && typeof data.error === 'string') {
      return data;
    }
  } catch {
    /* fallthrough */
  }
  return { error: `HTTP ${response.status}` };
}

async function request<T>(
  method: string,
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const url = joinUrl(API_BASE_URL, path);
  const headers: Record<string, string> = {};

  if (opts.requireAuth) {
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  let body: BodyInit | undefined;
  if (opts.json !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.json);
  } else if (opts.formData !== undefined) {
    // Let the browser set the multipart boundary in `Content-Type`.
    body = opts.formData;
  }

  const response = await fetch(url, { method, headers, body });

  if (response.status === 401) {
    const payload = await parseErrorBody(response);
    handleUnauthorized();
    throw new UnauthorizedError(payload.error);
  }

  if (!response.ok) {
    const payload = await parseErrorBody(response);
    throw new ApiError(payload.error, response.status, payload.field, payload.code);
  }

  if (opts.responseType === 'blob') {
    return (await response.blob()) as unknown as T;
  }

  // 204 / empty body safety net.
  if (response.status === 204) {
    return undefined as unknown as T;
  }

  const text = await response.text();
  if (text.length === 0) return undefined as unknown as T;
  return JSON.parse(text) as T;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Fetches the public projection for a single captain. No auth required.
 *
 * Validates: Requirements 1.1, 3.1
 */
export function getCaptain(id: string): Promise<PublicCaptain> {
  return request<PublicCaptain>('GET', `/api/captains/${encodeURIComponent(id)}`);
}

/**
 * Authenticates an admin and persists the returned JWT in `localStorage`.
 *
 * Validates: Requirements 4.1, 4.2
 */
export async function login(
  username: string,
  password: string,
): Promise<LoginResponse> {
  const data = await request<LoginResponse>('POST', '/api/auth/login', {
    json: { username, password },
  });
  if (data && typeof data.token === 'string' && data.token.length > 0) {
    setToken(data.token);
  }
  return data;
}

/**
 * Lists captains with pagination. Requires an admin JWT.
 *
 * Validates: Requirements 3.4, 8.3, 8.7
 */
export function listCaptains(params: {
  page: number;
  limit: number;
  department?: string;
}): Promise<Paginated<Captain>> {
  const searchParams: Record<string, string> = {
    page: String(params.page),
    limit: String(params.limit),
  };
  if (params.department) {
    searchParams.department = params.department;
  }
  const search = new URLSearchParams(searchParams);
  return request<Paginated<Captain>>('GET', `/api/captains?${search.toString()}`, {
    requireAuth: true,
  });
}

/**
 * Lists captains for the public directory. No auth required.
 */
export function listPublicCaptains(params: {
  page: number;
  limit: number;
  department?: string;
}): Promise<Paginated<PublicCaptain>> {
  const searchParams: Record<string, string> = {
    page: String(params.page),
    limit: String(params.limit),
  };
  if (params.department) {
    searchParams.department = params.department;
  }
  const search = new URLSearchParams(searchParams);
  return request<Paginated<PublicCaptain>>('GET', `/api/captains/public?${search.toString()}`);
}

/**
 * Creates a captain via multipart upload. Requires an admin JWT.
 *
 * Validates: Requirements 5.1, 5.4, 8.7
 */
export function createCaptain(form: FormData): Promise<Captain> {
  return request<Captain>('POST', '/api/captains', {
    requireAuth: true,
    formData: form,
  });
}

/**
 * Updates a captain via multipart upload. Requires an admin JWT.
 *
 * Validates: Requirements 6.1, 6.3, 8.7
 */
export function updateCaptain(id: string, form: FormData): Promise<Captain> {
  return request<Captain>('PUT', `/api/captains/${encodeURIComponent(id)}`, {
    requireAuth: true,
    formData: form,
  });
}

/**
 * Deletes a captain. Requires an admin JWT.
 *
 * Validates: Requirements 7.1, 8.7
 */
export function deleteCaptain(id: string): Promise<DeleteResponse> {
  return request<DeleteResponse>('DELETE', `/api/captains/${encodeURIComponent(id)}`, {
    requireAuth: true,
  });
}

/**
 * Fetches all distinct job titles. Requires an admin JWT.
 */
export function getJobTitles(): Promise<{ titles: string[] }> {
  return request<{ titles: string[] }>('GET', '/api/captains/job-titles', {
    requireAuth: true,
  });
}

/**
 * Fetches the QR PNG for a captain as a Blob. Requires an admin JWT.
 */
export function getQrPng(id: string): Promise<Blob> {
  return request<Blob>('GET', `/api/captains/${encodeURIComponent(id)}/qr`, {
    requireAuth: true,
    responseType: 'blob',
  });
}

// ---------------------------------------------------------------------------
// Default export: convenience namespace mirroring the design.md `ApiClient`
// ---------------------------------------------------------------------------

export const ApiClient = {
  getCaptain,
  login,
  listCaptains,
  listPublicCaptains,
  getJobTitles,
  createCaptain,
  updateCaptain,
  deleteCaptain,
  getQrPng,
  getToken,
  setToken,
  clearToken,
};

export default ApiClient;

// Re-export shared types so consumers can `import { Captain } from '../api/ApiClient'`.
export type {
  ApiErrorPayload,
  Captain,
  DeleteResponse,
  LoginResponse,
  Paginated,
  PublicCaptain,
} from './types';
